import type { ComponentType, ErrorInfo, FC } from "react";
import { Suspense } from "react";
import type { FormName } from "@searchui-searchforms/shared/config";
import { RemoteErrorBoundary } from "@/app/lib/RemoteErrorBoundary";
import type { ComponentProps } from "@/app/module-federation/lib/types";
import { FormSkeleton } from "@/shared/components/FormSkeleton";

interface EntryProps {
    Component: ComponentType<ComponentProps>;
    componentProps: ComponentProps;
    onError?: (error: unknown, errorInfo?: ErrorInfo) => void;
    formName?: FormName;
}

/**
 * The single root tree for a search form, used on every path: SSR (renderToHtml), browser
 * hydration (hydrateRoot), and CSR / post-hydration updates (createRoot, root.render). The path
 * is chosen by the caller's root API, not by this component.
 *
 * The top-level Suspense boundary ensures any suspension without a matching dehydrated cache
 * entry — a not-yet-found bug, a future new query, or a genuine race — degrades to a skeleton
 * instead of a permanent hydration stall or a hard #426 crash on interaction.
 *
 * This does not reduce SSR error visibility: recoverable errors caught by this boundary still
 * flow through renderToHtml's `recoverableErrors` array and are logged/reported by
 * renderForm.service.ts. The boundary changes classification (partial HTML + logged recoverable
 * error) rather than visibility.
 *
 * Note: RemoteErrorBoundary only catches errors during client-side rendering (after hydration).
 * Error boundaries do not catch rendering errors during SSR.
 */
export const RenderEntry: FC<EntryProps> = ({ Component, componentProps, onError, formName }) => (
    <RemoteErrorBoundary onError={onError} formMetadataParams={componentProps.formMetadataParams} formName={formName}>
        <Suspense fallback={<FormSkeleton />}>
            <Component {...componentProps} />
        </Suspense>
    </RemoteErrorBoundary>
);
